import { useTranslation } from '../../context/translationContext';
import {
  ConnectionAttributes,
  ConnectionAttributesTranslated,
  DocumentStatus,
  DocumentStatusTranslated,
} from '../../types/Invoice';
import { PaymentStatus, PaymentStatusTranslated } from '../../types/Payment';

export enum ListButtonEntityType {
  DOCUMENT_STATUS = 'DOCUMENT_STATUS',
  CONNECTION_ATTRIBUTES = 'CONNECTION_ATTRIBUTES',
  PAYMENT_STATUS = 'PAYMENT_STATUS',
}

interface Props {
  params: any;
  entityType: ListButtonEntityType;
}

/**
 * List button component to view statuses of entities
 */
export default function ListButton({ params, entityType }: Props) {
  const { t } = useTranslation();
  const baseClass = `inline-block rounded-full min-w-max border-2 border-primary px-3 pt-0.5 text-xs font-medium leading-normal transition duration-150 ease-in-out hover:border-primary-600 hover:bg-neutral-500 hover:bg-opacity-10 hover:text-primary-600 focus:border-primary-600 focus:text-primary-600 focus:outline-none focus:ring-0 active:border-primary-700 active:text-primary-700 dark:hover:bg-neutral-100 dark:hover:bg-opacity-10`;

  /**
   * Function for generating status indicators with translated name
   * @param status
   */
  const getDocumentStatusBtn = (status: DocumentStatus) => {
    let itemClass = ``;
    switch (status) {
      case DocumentStatus.CONCEPT:
        itemClass = `border border-yellow-500 text-yellow-500`;
        break;
      case DocumentStatus.ISSUED:
        itemClass = `border border-green-500 text-green-500`;
        break;
      case DocumentStatus.BOOKED_LOCAL:
        itemClass = `border border-blue-500 text-blue-500`;
        break;
      case DocumentStatus.EXPORTED:
        itemClass = `border border-green-500 text-green-500`;
        break;
      case DocumentStatus.CANCELED:
        itemClass = `border border-red-500 text-red-500`;
        break;
      case DocumentStatus.TO_BE_MINED:
        itemClass = `border border-blue-500 text-blue-500`;
        break;
      case DocumentStatus.TO_BE_MINED_AUTOMATICALLY:
        itemClass = `border border-blue-500 text-blue-500`;
        break;
    }
    return (
      <span className={`${itemClass} ${baseClass}`}>{t(DocumentStatusTranslated[status])}</span>
    );
  };

  /**
   * Function for generating info about connections with other documents
   * @param attr - array of connection attributes
   */
  const getConnectionAttributes = (attr: string[]) => {
    if (attr.includes(ConnectionAttributes.FULLY_CONNECTED))
      return (
        <span className={`border border-green-500 text-green-500 ${baseClass}`}>
          {t(ConnectionAttributesTranslated[ConnectionAttributes.FULLY_CONNECTED])}
        </span>
      );
    if (attr.includes(ConnectionAttributes.PARTIALLY_CONNECTED))
      return (
        <span className={`border border-orange-500 text-orange-500 ${baseClass}`}>
          {t(ConnectionAttributesTranslated[ConnectionAttributes.PARTIALLY_CONNECTED])}
        </span>
      );
    return <></>;
  };

  /**
   * Function for generating info about payment status
   * @param attr - array of connection attributes
   */
  const getPaymentStatus = (paymentStatus: PaymentStatus) => {
    let itemClass = ``;
    switch (paymentStatus) {
      case PaymentStatus.NOT_PAID:
        itemClass = `border border-red-500 text-red-500`;
        break;
      case PaymentStatus.PARTIALLY_PAID:
        itemClass = `border border-orange-500 text-orange-500`;
        break;
      case PaymentStatus.PAID:
        itemClass = `border border-green-500 text-green-500`;
        break;
      case PaymentStatus.OVERPAID:
        itemClass = `border border-orange-500 text-orange-500`;
        break;

      default:
        break;
    }
    return (
      <span className={`${itemClass} ${baseClass}`}>
        {t(PaymentStatusTranslated[paymentStatus])}
      </span>
    );
  };

  if (entityType === ListButtonEntityType.DOCUMENT_STATUS) return getDocumentStatusBtn(params);
  if (entityType === ListButtonEntityType.CONNECTION_ATTRIBUTES)
    return getConnectionAttributes(params);
  if (entityType === ListButtonEntityType.PAYMENT_STATUS) return getPaymentStatus(params);
  return <></>;
}
